import { Typography } from "@mui/joy";
import React, { useState } from "react";
import useApiService from "../../../hooks/useApiService";
import { ClientRecord, DocumentMeta } from "../../../models/ClientInfo";
import { RouteComponentProps } from "../../../models/RouteComponent";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CancelIcon, CheckIcon, Eye, InfoCircleIcon, Trash, Upload } from "../../../assets/icons";
import { Paginator } from "primereact/paginator";
import { formatDate } from "../../../utils/DateUtils";
import { Dropdown } from "primereact/dropdown";
import DocumentDetails from "../../../components/DocumentDetails";
import { Dialog } from "primereact/dialog";
import { useToast } from "../../../context/ToastContext";
import { downloadFile, removeMetaDocument } from "../../../service/documentsService";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

type PaginationType = {
  first: number;
  rows: number;
  page: number;
  totalPages: number;
};

function CompanyDocuments(props: RouteComponentProps) {
  const { session } = props;
  const toast = useToast();

  const [paginationParams, setPaginationParams] = useState({
    first: 0,
    rows: 10,
    page: 0,
  });
  const [selectedDocument, setSelectedDocument] = useState<DocumentMeta>();
  const [modalToShow, setModalToShow] = useState<"upload" | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const {
    data,
    loading: loadingMeta,
    fetchData: fetchMetaDocuments,
  } = useApiService<{
    content: DocumentMeta[];
    totalRecords: number;
  }>({
    method: "GET",
    url: "meta",
    includeTotal: true,
    params: {
      record_id: session.client_id,
      rows: paginationParams.rows,
      page: paginationParams.page,
    },
    apiName: "core",
    onSuccess: data => {
      setTotalRecords(data.totalRecords);
    },
  });

  const handlePagination = async (e: PaginationType) => {
    console.log("handlePagination", e);
    setPaginationParams({
      first: e.first,
      rows: e.rows,
      page: e.page,
    });

    await fetchMetaDocuments({
      fetchParams: {
        rows: e.rows,
        page: e.page,
      },
    });
  };

  const download = async (document: DocumentMeta) => {
    await downloadFile(document.name, document.s3_key);
  };

  const deleteDocument = async (document: DocumentMeta) => {
    props.setGlobalLoader(true);
    await removeMetaDocument(document.id);
    props.setGlobalLoader(false);
    toast("success", "Document Deleted Successfully");
    await fetchMetaDocuments({
      fetchParams: {
        rows: paginationParams.rows,
        page: paginationParams.page,
      },
    });
  };

  const deleteConfirm = (row: DocumentMeta) => {
    confirmDialog({
      message: "Are you sure you want to proceed?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => deleteDocument(row),
    });
  };

  const renderStatus = (row: DocumentMeta) => {
    switch (row.status) {
      case "success":
        return <CheckIcon fill={"#2DD36FFF"} />;
      case "pending":
        return <InfoCircleIcon className={"warning-icon"} />;
      case "failed":
        return <CancelIcon fill={"#EB445AFF"} />;
      default:
        return "—";
    }
  };

  const renderRequired = (row: DocumentMeta) => {
    switch (row.required) {
      case true:
        return <CheckIcon fill={"#2DD36FFF"} />;
      case false:
      default:
        return <CancelIcon fill={"#EB445AFF"} />;
    }
  };

  const renderExpireAt = (row: DocumentMeta) => {
    if (!row.expire_at) return "—";
    return formatDate(row.expire_at);
  };

  const renderUploadedOn = (row: DocumentMeta) => {
    if (!row.uploaded_on) return "—";
    return formatDate(row.uploaded_on);
  };

  const optionTemplate = (option: { label: string; value: string }) => {
    switch (option.value) {
      case "view":
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <Eye />
            <div>{option.label}</div>
          </div>
        );
      case "upload":
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <Upload />
            <div>{option.label}</div>
          </div>
        );

      case "delete":
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <Trash />
            <div>{option.label}</div>
          </div>
        );
      default:
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <div>{option.label}</div>
          </div>
        );
    }
  };

  const displaySettings = (rowData: DocumentMeta) => {
    let options = [{ label: "Upload", value: "upload", id: rowData.id, name: "upload" }];

    if (rowData.status === "success") {
      options = [
        ...options,
        { label: "Delete", value: "delete", id: rowData.id, name: "delete" },
        { label: "View", value: "view", id: rowData.id, name: "view" },
      ];
    }

    return (
      <div>
        <Dropdown
          dropdownIcon={"pi pi-cog"}
          placeholder=" "
          className="p-dropdown-icon-only"
          style={{
            border: "none",
            background: "transparent",
            boxShadow: "none",
            minWidth: "auto",
          }}
          showClear={false}
          options={options}
          itemTemplate={optionTemplate}
          onChange={async e => {
            if (e.value === "upload") {
              setSelectedDocument(_ => {
                setTimeout(() => {
                  setModalToShow("upload");
                }, 50);
                return rowData;
              });
              return;
            }
            if (e.value === "delete") {
              deleteConfirm(rowData);
              return;
            }
            if ((e.value = "view")) {
              await download(rowData);
              return;
            }
          }}
          panelStyle={{ width: "350px" }}
        />
      </div>
    );
  };

  return (
    <>
      <Typography level="h2" component="h1">
        Company Documents
      </Typography>
      <DataTable
        value={data?.content ?? []}
        className={`policies-procedures-table`}
        tableStyle={{ minWidth: "50rem" }}
        loading={loadingMeta}
        lazy={true}
        totalRecords={totalRecords}
        onPage={async page => {
          // @ts-ignore
          await handlePagination(page as PaginationType);
        }}
        rowsPerPageOptions={[5, 10, 20, 50]}
        first={paginationParams.first}
        rows={paginationParams.rows}
        paginator={true}
      >
        <Column header={"Document name"} field={"name"} sortable={true} />
        <Column header={"Status"} field={"status"} body={renderStatus} />
        <Column header={"Required"} field={"required"} body={renderRequired} />
        <Column header={"Expire on"} field={"expire_at"} body={renderExpireAt} />
        <Column header={"Uploaded on"} field={"uploaded_on"} body={renderUploadedOn} />
        <Column field={"_"} body={displaySettings} />
      </DataTable>
      <Dialog
        header={
          selectedDocument?.name ? `Upload ${selectedDocument.name} Document` : "Add Document"
        }
        visible={modalToShow === "upload"}
        style={{ width: "50vw" }}
        onHide={() => setModalToShow(null)}
      >
        <DocumentDetails
          setGlobalLoading={props.setGlobalLoader}
          record={selectedDocument}
          id={selectedDocument?.id}
          record_id={session.client_id}
          toast={toast}
          callback={async () => {
            setModalToShow(null);
            await fetchMetaDocuments({
              fetchParams: {
                rows: paginationParams.rows,
                page: paginationParams.page,
              },
            });
            toast("success", "Document successfully uploaded");
          }}
        />
      </Dialog>
      <ConfirmDialog />
    </>
  );
}

export default CompanyDocuments;
